import { Injectable } from '@angular/core';
import {
  VideoProject,
  VideoProjectData,
  VideoShot,
} from '@models/Sessions/Idea/VideoProject';
import { RequestService } from '@services/request.service';
import { MessageService } from 'primeng/api';
import { interval, switchMap } from 'rxjs';

export interface GetVideoProjectParams {
  sessionId: string;
  ideaId: string;
}

export interface GetVideoProjectDto {
  videoProject: VideoProject;
}

export interface PutVideoProjectParams {
  sessionId: string;
  ideaId: string;
}

export interface PutVideoProjectDto {
  videoProject: VideoProject;
}

export interface PatchVideoProjectParams {
  sessionId: string;
  ideaId: string;
}

export interface PatchVideoProjectDto {
  videoProject: VideoProject;
}

export interface PostVideoShotGenerateParams {
  sessionId: string;
  ideaId: string;
  shotId: string;
}

export interface PostVideoClipGenerateDto {
  shot: VideoShot;
}

export interface DeleteVideoClipParams {
  sessionId: string;
  ideaId: string;
  shotId: string;
  clipId: string;
}

@Injectable()
export class VideoProjectService {
  constructor(
    private readonly client: RequestService,
    private readonly messageService: MessageService,
  ) {}

  polling(params: GetVideoProjectParams) {
    return interval(10000).pipe(switchMap(() => this.get(params)));
  }

  get({ sessionId, ideaId }: GetVideoProjectParams) {
    return this.client.get<GetVideoProjectDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/video`,
      handlers: {
        success: () => {
          /** noop */
        },
        error: () => {
          /** noop */
        },
      },
    });
  }

  upsert({ sessionId, ideaId }: PutVideoProjectParams) {
    return this.client.put<undefined, PutVideoProjectDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/video`,
      body: undefined,
      handlers: {
        success: () =>
          this.messageService.add({
            severity: 'success',
            summary: 'Video Project has been created successfully.',
            life: 3000,
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to create an Video Project',
            life: 3000,
          }),
      },
    });
  }

  update(
    { sessionId, ideaId }: PatchVideoProjectParams,
    body: VideoProjectData,
  ) {
    return this.client.patch<VideoProjectData, PatchVideoProjectDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/video`,
      body,
      handlers: {
        success: () =>
          this.messageService.add({
            severity: 'success',
            summary: 'Video Project has been updated successfully.',
            life: 3000,
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to update an Video Project',
            life: 3000,
          }),
      },
    });
  }

  generateClip({ sessionId, ideaId, shotId }: PostVideoShotGenerateParams) {
    return this.client.post<void, PostVideoClipGenerateDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/video/shots/${shotId}/generate`,
      body: void 0,
      handlers: {
        success: () =>
          this.messageService.add({
            severity: 'info',
            summary: 'Video Shot asset is being generating...',
            life: 3000,
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to generate an Video Shot asset',
            life: 3000,
          }),
      },
    });
  }
}
